import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ClipboardList,
  DoorOpen,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  Thermometer,
  XCircle,
} from "lucide-react";
import TopBar from "../../components/TopBar";

async function getJson(url) {
  const res = await fetch(url, { credentials: "include" });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

async function postJson(url, body) {
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.ok) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

function statusMeta(status) {
  if (status === "critical") {
    return {
      label: "Critica",
      icon: <XCircle size={16} />,
      className: "bg-red-50 text-red-700 border-red-200",
    };
  }
  if (status === "warning") {
    return {
      label: "Attenzione",
      icon: <AlertTriangle size={16} />,
      className: "bg-amber-50 text-amber-700 border-amber-200",
    };
  }
  return {
    label: "Regolare",
    icon: <CheckCircle2 size={16} />,
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
}

function Stat({ icon, label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-900",
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    blue: "bg-sky-50 border-sky-200 text-sky-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };

  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-white/80 border border-white grid place-items-center">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-2xl font-bold leading-none">{value}</div>
          <div className="mt-1 text-sm opacity-75 truncate">{label}</div>
        </div>
      </div>
    </div>
  );
}

function LocationRow({ location, current, busy, onEnter }) {
  const meta = statusMeta(location.status);
  const m = location.metrics || {};

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900 truncate">
              {location.nome}
            </h2>
            <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.className}`}>
              {meta.icon}
              {meta.label}
            </span>
            {current ? (
              <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700">
                Contesto attuale
              </span>
            ) : null}
          </div>

          {location.indirizzo ? (
            <div className="mt-1 flex items-center gap-1 text-sm text-slate-500">
              <MapPin size={14} />
              <span className="truncate">{location.indirizzo}</span>
            </div>
          ) : null}

          {Array.isArray(location.alerts) && location.alerts.length ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {location.alerts.map((alert) => (
                <span key={alert} className="rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-700">
                  {alert}
                </span>
              ))}
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500">Nessuna anomalia evidente.</div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:w-[560px]">
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-xs text-slate-500">Dispositivi</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {m.devices_active_24h || 0}/{m.devices_total || 0}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-xs text-slate-500">Attivita aperte</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{m.open_tasks || 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-xs text-slate-500">NC aperte</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">{m.non_conformita_open || 0}</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="text-xs text-slate-500">Oggi</div>
            <div className="mt-1 text-lg font-semibold text-slate-900">
              {(m.ricevimento_today || 0) + (m.abbattimenti_today || 0) + (m.etichette_today || 0)}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => onEnter(location.id)}
          disabled={busy}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#043662] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#032f56] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {busy ? <Loader2 size={17} className="animate-spin" /> : <DoorOpen size={17} />}
          Entra
        </button>
      </div>
    </div>
  );
}

export default function MultiLocationDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getJson("/api/multilocation/overview.php"));
    } catch (err) {
      setError(err.message || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const locations = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.locations || []).filter((item) => {
      const matchesStatus = status === "all" || item.status === status;
      const matchesQuery =
        !q ||
        String(item.nome || "").toLowerCase().includes(q) ||
        String(item.indirizzo || "").toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [data, query, status]);

  const enterLocation = async (aziendaId) => {
    setBusyId(aziendaId);
    setError("");
    try {
      await postJson("/api/multilocation/switch.php", { azienda_id: aziendaId });
      navigate("/");
      window.location.reload();
    } catch (err) {
      setError(err.message || "Cambio location non riuscito");
      setBusyId(null);
    }
  };

  const summary = data?.summary || {};

  return (
    <div className="min-h-screen bg-slate-100">
      <TopBar title="Gestione multilocation" user={{ name: "Gestore" }} />

      <main className="mx-auto max-w-screen-2xl px-4 py-6 lg:px-6">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              <Building2 size={16} />
              Multilocation
            </div>
            <h1 className="mt-2 text-3xl font-bold text-slate-950">
              Situazione location
            </h1>
          </div>

          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Aggiorna
          </button>
        </div>

        {error ? (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <Stat icon={<Building2 size={20} />} label="Location" value={summary.locations || 0} />
          <Stat icon={<XCircle size={20} />} label="Critiche" value={summary.critical || 0} tone="red" />
          <Stat icon={<AlertTriangle size={20} />} label="Attenzione" value={summary.warnings || 0} tone="amber" />
          <Stat icon={<ClipboardList size={20} />} label="Attivita aperte" value={summary.open_tasks || 0} tone="blue" />
          <Stat
            icon={<Thermometer size={20} />}
            label="Sonde attive"
            value={`${summary.active_devices || 0}/${summary.total_devices || 0}`}
            tone="green"
          />
        </div>

        <div className="mt-5 flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-300 pl-10 pr-3 text-sm outline-none focus:border-[#043662] focus:ring-2 focus:ring-sky-100"
              placeholder="Cerca location"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto">
            {[
              ["all", "Tutte"],
              ["critical", "Critiche"],
              ["warning", "Attenzione"],
              ["ok", "Regolari"],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setStatus(value)}
                className={[
                  "h-10 rounded-lg px-3 text-sm font-semibold transition",
                  status === value
                    ? "bg-[#043662] text-white"
                    : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                ].join(" ")}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {loading ? (
            <div className="grid min-h-[220px] place-items-center rounded-lg border border-slate-200 bg-white">
              <Loader2 size={26} className="animate-spin text-slate-400" />
            </div>
          ) : locations.length ? (
            locations.map((location) => (
              <LocationRow
                key={location.id}
                location={location}
                current={Number(data?.current_azienda_id) === Number(location.id)}
                busy={busyId === location.id}
                onEnter={enterLocation}
              />
            ))
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
              Nessuna location trovata.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
